import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import pdfGenerator from '../../../services/pdfGenerator';
import oracleSessionService from '../../../services/oracleSessionService';
import { getRayDisplayName, getTypeDisplayName, getElementDisplayName } from '../../../constants/oracleMysticalMappings';
import './MasterReview.css';

const MasterReview = ({ sessionData, onBackToDashboard, onNavigation }) => {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingDecree, setIsGeneratingDecree] = useState(false);
  const [isSavingSession, setIsSavingSession] = useState(false);
  const [sessionSaved, setSessionSaved] = useState(false);
  
  const { user } = useAuth();

  // Save session to database when MasterReview loads
  useEffect(() => {
    const saveSessionToDatabase = async () => {
      if (!user || sessionSaved || isSavingSession || !sessionData?.sessionId) return;
      
      setIsSavingSession(true);
      
      try {
        const result = await oracleSessionService.saveCompletedSession(sessionData, user.id);
        
        if (result.success) {
          setSessionSaved(true);
        } else {
          console.error('Failed to save oracle session:', result.error);
          // Show error in console but also show to user for debugging
          alert(`Erro ao salvar sessão: ${result.error}`);
        }
      } catch (error) {
        console.error('Error saving oracle session:', error);
      } finally {
        setIsSavingSession(false);
      }
    };

    saveSessionToDatabase();
  }, [user?.id, sessionData?.sessionId]); // Only depend on user ID and session ID to prevent duplicates

  const generatePDF = async () => {
    if (isGeneratingPDF) return;
    
    try {
      setIsGeneratingPDF(true);
      
      // Generate and download PDF with new filename format (no custom filename needed)
      pdfGenerator.downloadPDF(sessionData);
      
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      alert('Erro ao gerar PDF. Tente novamente.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const generateDecreePrintout = async () => {
    if (isGeneratingDecree) return;
    
    try {
      setIsGeneratingDecree(true);
      
      // Check if decree is available
      if (!sessionData.decreeText) {
        alert('Decreto não disponível para impressão.');
        return;
      }
      
      // Generate and download A2 decree printout
      pdfGenerator.downloadDecreePrintout(sessionData);
      
    } catch (error) {
      console.error('Erro ao gerar impressão do decreto:', error);
      alert('Erro ao gerar impressão do decreto. Tente novamente.');
    } finally {
      setIsGeneratingDecree(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  return (
    <div className="oracle-step master-review-step">
      <div className="review-header">
        <h2>Sua Consulta ao Oráculo dos 12 Raios</h2>
        <div className="session-info">
          <span>Sessão concluída em {formatDate(sessionData.completedAt || sessionData.timestamp)}</span>
          <span>•</span>
          {/* TD3-120: Theme display hidden per alpha testing feedback */}
          <span>{getRayDisplayName(sessionData.raySelection)}</span>
          {isSavingSession && <span>• Salvando...</span>}
          {sessionSaved && <span>• ✓ Salvo</span>}
        </div>
      </div>

      {/* Content Sections */}
      <div className="review-content">
        
        {/* Blockage Section */}
        <section id="blockage" className="review-section blockage-section">
          <div className="section-header">
            <h3>Seu Bloqueio</h3>
            <div className="section-selections">
              <span>{getRayDisplayName(sessionData.raySelection)}</span>
              <span>{getTypeDisplayName(sessionData.diagnosisType)}</span>
              <span>Elemento {getElementDisplayName(sessionData.diagnosisElement)}</span>
            </div>
          </div>
          
          <div className="section-content">
            <div className="oracle-text-display">
              {sessionData.blockageText ? 
                sessionData.blockageText : 
                <div className="missing-content-warning">
                  <p>⚠️ Conteúdo de bloqueio não foi gerado.</p>
                  <small>O oráculo pode ter encontrado dificuldades durante a geração do conteúdo.</small>
                </div>
              }
            </div>
            
            <div className="user-question-reminder">
              <h4>Sua Pergunta</h4>
              <p>"{sessionData.userQuestion}"</p>
            </div>
          </div>
        </section>

        {/* Solution Section */}
        <section id="solution" className="review-section solution-section">
          <div className="section-header">
            <h3>Para Desbloquear</h3>
            <div className="section-selections">
              <span>{getRayDisplayName(sessionData.raySelection)}</span>
              <span>{getTypeDisplayName(sessionData.solutionType)}</span>
              <span>Elemento {getElementDisplayName(sessionData.solutionElement)}</span>
            </div>
          </div>
          
          <div className="section-content">
            <div className="oracle-text-display">
              {sessionData.unblockText ? 
                sessionData.unblockText : 
                <div className="missing-content-warning">
                  <p>⚠️ Conteúdo de solução não foi gerado.</p>
                  <small>O oráculo pode ter encontrado dificuldades durante a geração do conteúdo.</small>
                </div>
              }
            </div>
          </div>
        </section>

        {/* Wisdom Section */}
        <section id="wisdom" className="review-section wisdom-section">
          <div className="section-header">
            <h3>Sabedoria e Decreto</h3>
          </div>
          
          <div className="section-content">
            <div className="wisdom-content">
              <div className="wisdom-text-display">
                <h4>Sabedoria</h4>
                <div className="oracle-text-display">
                  {sessionData.wisdomText ? 
                    sessionData.wisdomText : 
                    <div className="missing-content-warning">
                      <p>⚠️ Conteúdo de sabedoria não foi gerado.</p>
                      <small>O oráculo pode ter encontrado dificuldades durante a geração do conteúdo.</small>
                    </div>
                  }
                </div>
              </div>
              
              <div className="decree-display">
                <h4>Seu Decreto Pessoal</h4>
                <div className="decree-text-highlight">
                  {sessionData.decreeText ? 
                    `"${sessionData.decreeText}"` : 
                    <div className="missing-content-warning">
                      <p>⚠️ Decreto não foi gerado.</p>
                      <small>O oráculo pode ter encontrado dificuldades durante a geração do conteúdo.</small>
                    </div>
                  }
                </div>
                {sessionData.decreeText && (
                  <small>Repita este decreto diariamente para fortalecer sua transformação</small>
                )}
              </div>
            </div>
          </div>
        </section>

      </div>

      {/* Action Buttons */}
      <div className="review-actions">
        <div className="save-instructions">
          <p>Para guardar sua consulta, tire uma captura de tela ou use os botões abaixo para gerar um PDF para impressão:</p>
        </div>
        
        <div className="print-actions">
          <button
            onClick={generatePDF}
            className={`download-pdf-btn secondary-cta ${isGeneratingPDF ? 'generating' : ''}`}
            disabled={isGeneratingPDF}
          >
            {isGeneratingPDF ? '⏳ Gerando...' : '📄 Baixar PDF Completo'}
          </button>

          <button
            onClick={generateDecreePrintout}
            className={`decree-printout-btn secondary-cta ${isGeneratingDecree ? 'generating' : ''}`}
            disabled={isGeneratingDecree || !sessionData.decreeText}
            title={!sessionData.decreeText ? 'Decreto não disponível' : 'Imprimir 2 decretos A5 empilhados em A4 (corte horizontal)'}
          >
            {isGeneratingDecree ? '⏳ Gerando...' : '🖨️ Baixar PDF do Decreto'}
          </button>
        </div>
        
        <div className="navigation-actions">
          <button onClick={onBackToDashboard} className="back-dashboard-btn primary-cta full-width">
            ✨ Voltar ao Painel para Novo Oráculo ou Consultas Anteriores
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="session-summary">
        <div className="summary-grid">
          <div className="summary-item">
            <span className="summary-label">Raio Escolhido</span>
            <span className="summary-value">{getRayDisplayName(sessionData.raySelection)}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Cartas Selecionadas</span>
            <span className="summary-value">
              {getTypeDisplayName(sessionData.diagnosisType)}/{getElementDisplayName(sessionData.diagnosisElement)} + {getTypeDisplayName(sessionData.solutionType)}/{getElementDisplayName(sessionData.solutionElement)}
            </span>
          </div>
          {/* TD3-120: Theme section hidden for beta - will be enabled when theme selection is implemented */}
          {/* <div className="summary-item">
            <span className="summary-label">Tema</span>
            <span className="summary-value">{sessionData.themeId || 'Basic'}</span>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default MasterReview;